import { JSONSchema7TypeName } from 'json-schema'

import { PropertySchema } from './types.ts'

export const createEmptySchema = (): PropertySchema => ({
  type: 'object',
  properties: {},
  required: [],
})

export const addProperty = (
  schema: PropertySchema,
  name: string,
  type: JSONSchema7TypeName
): PropertySchema => ({
  ...schema,
  properties: {
    ...schema.properties,
    [name]: { type, description: '' },
  },
})

export const updateProperty = (
  schema: PropertySchema,
  path: string[],
  updatedProperty: PropertySchema
): PropertySchema => {
  if (path.length === 0) {
    return { ...schema, ...updatedProperty }
  }

  const updateRecursive = (
    obj: Record<string, PropertySchema>,
    [head, ...rest]: string[]
  ): Record<string, PropertySchema> => {
    const current = obj[head]

    if (rest.length === 0) {
      return { ...obj, [head]: { ...current, ...updatedProperty } }
    }

    if (current.type === 'array') {
      return {
        ...obj,
        [head]: {
          ...current,
          items: updateRecursive(
            { items: current.items } as Record<string, PropertySchema>,
            rest
          ).items,
        },
      }
    }

    return {
      ...obj,
      [head]: {
        ...current,
        ...(current.properties
          ? { properties: updateRecursive(current.properties || {}, rest) }
          : {}),
      },
    }
  }

  return {
    ...schema,
    ...(schema.properties
      ? { properties: updateRecursive(schema.properties, path) }
      : {}),
  }
}

export const removeProperty = (
  schema: PropertySchema,
  path: string[]
): PropertySchema => {
  if (path.length === 0) {
    return schema
  }

  const removeRecursive = (
    obj: Record<string, PropertySchema>,
    [head, ...rest]: string[]
  ): Record<string, PropertySchema> => {
    if (rest.length === 0) {
      const { [head]: _, ...remaining } = obj
      return remaining
    }

    const current = obj[head]
    if (current.type === 'array') {
      return {
        ...obj,
        [head]: {
          ...current,
          items: removeRecursive(
            { items: current.items } as Record<string, PropertySchema>,
            rest
          ).items,
        },
      }
    }

    return {
      ...obj,
      [head]: {
        ...current,
        ...(current.properties
          ? { properties: removeRecursive(current.properties || {}, rest) }
          : {}),
      },
    }
  }

  return {
    ...schema,
    ...(schema.properties
      ? { properties: removeRecursive(schema.properties ?? {}, path) }
      : {}),
  }
}

export const toggleRequired = (
  schema: PropertySchema,
  path: string[],
  isRequired: boolean
): PropertySchema => {
  if (path.length === 0) {
    return schema
  }

  // For top-level properties, modify the root schema's required array
  if (path.length === 1) {
    const currentRequired = schema.required || []
    const fieldName = path[0]

    const updatedRequired = isRequired
      ? [...new Set([...currentRequired, fieldName])]
      : currentRequired.filter((item) => item !== fieldName)

    return {
      ...schema,
      required: updatedRequired,
    }
  }

  const toggleRecursive = (
    obj: Record<string, PropertySchema>,
    [head, ...rest]: string[]
  ): Record<string, PropertySchema> => {
    const current = obj[head]

    if (!current) {
      return obj
    }

    if (rest.length === 1) {
      // We're at the parent level of the target field
      const parentRequired = current.required || []
      const fieldName = rest[0]

      const updatedRequired = isRequired
        ? [...new Set([...parentRequired, fieldName])]
        : parentRequired.filter((item) => item !== fieldName)

      return {
        ...obj,
        [head]: {
          ...current,
          required: updatedRequired,
        },
      }
    }

    if (current.type === 'array') {
      return {
        ...obj,
        [head]: {
          ...current,
          items: toggleRecursive(
            { items: current.items } as Record<string, PropertySchema>,
            rest
          ).items,
        },
      }
    }

    return {
      ...obj,
      [head]: {
        ...current,
        ...(current.properties
          ? { properties: toggleRecursive(current.properties, rest) }
          : {}),
      },
    }
  }

  return {
    ...schema,
    ...(schema.properties
      ? { properties: toggleRecursive(schema.properties, path) }
      : {}),
  }
}
